import React, { useEffect, useRef, useState } from 'react';
import { FiFilm, FiMaximize2, FiVolume2, FiVolumeX } from 'react-icons/fi';
import { fullUrl } from '../../api';
import { useLanguage } from '../../i18n';
import { formatTime, isVertical, ratioFromSize } from '../../utils/media';
import { playSfx } from '../../utils/sfx';
import { claimPlayback, playClip, releasePlayback, useVideoSound } from '../../utils/videoSound';
import { BLUE } from './brand';

/**
 * A video the author **uploaded** (compressed MP4) inside a Facebook-like post.
 *
 * The card follows the clip: landscape stays 16:9, a **vertical clip becomes a
 * 9:16 Reel** (phone-width, centred) and shows its poster frame until played.
 *
 * `autoPlayOnView` (used in the article feed) plays the clip as soon as ~55 % of
 * it is on screen and **stops it as soon as it scrolls away** — the clip you see
 * is the clip that plays. Sound follows the shared preference (`ms_reel_sound`):
 * the next clip then also auto-plays **with sound**; if the browser blocks it,
 * the clip plays muted and a “tap once to turn the sound on” pill appears.
 * `inline` renders the player straight away (used by the Reels feed).
 */
export default function UploadedVideo({ post, className = '', inline = false, autoPlayOnView = false }) {
  const { t } = useLanguage();
  const [openRatio, setOpenRatio] = useState(false);
  const [soundOn, setSoundOn] = useVideoSound();   // shared with Reels + stories
  const [blocked, setBlocked] = useState(false);   // browser refused unmuted autoplay
  const video = useRef(null);
  const pausedByUser = useRef(false);
  const inView = useRef(false);
  const soundRef = useRef(soundOn);
  soundRef.current = soundOn;

  const src = post?.video && (post.video.startsWith('http') ? post.video : fullUrl(post.video));

  // Play the clip that is on screen, stop the ones that are not.
  useEffect(() => {
    if (!autoPlayOnView) return undefined;
    const el = video.current;
    if (!el) return undefined;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && entry.intersectionRatio > 0.55) {
          inView.current = true;
          if (pausedByUser.current) return;              // the viewer stopped this one
          claimPlayback(el);
          playClip(el, soundRef.current).then((st) => setBlocked(st === 'blocked'));
        } else {
          inView.current = false;
          pausedByUser.current = false;                 // scrolled away → may play again
          releasePlayback(el);
          el.pause();                                   // never keep an unseen video running
        }
      });
    }, { threshold: [0.15, 0.6] });
    io.observe(el);
    return () => { io.disconnect(); releasePlayback(el); el.pause(); };
  }, [autoPlayOnView, src]);

  // Sound switch (shared): apply it to this clip — and keep it playing.
  useEffect(() => {
    if (!autoPlayOnView) return;
    const el = video.current;
    if (!el) return;
    el.muted = !soundOn;
    if (!inView.current || pausedByUser.current) return;
    if (el.paused) {
      claimPlayback(el);
      playClip(el, soundOn).then((st) => setBlocked(st === 'blocked'));
    } else {
      setBlocked(false);
    }
  }, [soundOn, autoPlayOnView]);

  // The browser refused sound because nobody had touched the page yet: the first
  // tap anywhere unlocks it → unmute the clip that is on screen automatically.
  useEffect(() => {
    if (!blocked || !soundOn || !autoPlayOnView) return undefined;
    const unlock = () => {
      const el = video.current;
      if (!el || !inView.current) return;
      playClip(el, true).then((st) => { if (st !== 'blocked') setBlocked(false); });
    };
    document.addEventListener('pointerdown', unlock);
    return () => document.removeEventListener('pointerdown', unlock);
  }, [blocked, soundOn, autoPlayOnView]);

  /** Speaker button: remembers the choice for every video and starts playing sound. */
  const toggleSound = async (e) => {
    e.stopPropagation();
    const next = !soundOn;
    setSoundOn(next);
    playSfx(next ? 'toggle-on' : 'toggle-off');
    if (!next) return;                     // muting: the effect above silences the clip
    const el = video.current;
    if (!el) return;
    claimPlayback(el);
    pausedByUser.current = false;
    const st = await playClip(el, true);    // turning the sound on also starts playback
    setBlocked(st === 'blocked');
  };

  const poster = post.video_thumb
    ? (post.video_thumb.startsWith('http') ? post.video_thumb : fullUrl(post.video_thumb))
    : undefined;
  const vertical = isVertical(post.video_width, post.video_height);
  const ratio = ratioFromSize(post.video_width, post.video_height);
  const reel = post.is_reel ?? vertical;

  if (!src) return null;

  return (
    <div className={`${className} ${vertical ? 'flex justify-center' : ''}`}>
      <div className="relative w-full" style={{ maxWidth: vertical ? 340 : undefined }}>
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <video
          ref={video}
          src={src}
          poster={poster}
          controls
          playsInline
          loop={autoPlayOnView}
          preload="metadata"
          onClick={(e) => e.stopPropagation()}
          onPlay={() => {
            pausedByUser.current = false;
            claimPlayback(video.current);        // a manual play also stops the previous media
          }}
          onPause={() => { if (autoPlayOnView && inView.current) pausedByUser.current = true; }}
          className="rounded-xl bg-black w-full transition-opacity duration-300"
          style={{
            aspectRatio: ratio,
            maxHeight: vertical ? '80vh' : undefined,
            objectFit: inline ? 'contain' : (openRatio ? 'contain' : 'cover'),
          }}
        />

        {reel && (
          <span
            className="absolute top-2 left-2 text-[10px] font-extrabold px-2 py-0.5 rounded-full text-white inline-flex items-center gap-1"
            style={{ background: BLUE }}
          >
            <FiFilm className="w-3 h-3" /> {t('reelBadge')}
          </span>
        )}

        {post.video_duration > 0 && (
          <span className="absolute bottom-2 right-12 text-[10px] font-bold px-1.5 py-0.5 rounded bg-black/60 text-white">
            {formatTime(post.video_duration)}
          </span>
        )}

        {autoPlayOnView && (
          <button
            type="button"
            data-sfx="none"
            onClick={toggleSound}
            className="absolute bottom-2 left-2 p-1.5 rounded-lg bg-black/55 text-white transition"
            aria-label={soundOn ? t('mute') : t('unmute')}
            title={soundOn ? t('mute') : t('unmute')}
          >
            {soundOn ? <FiVolume2 className="w-4 h-4" /> : <FiVolumeX className="w-4 h-4" />}
          </button>
        )}

        {/* the browser blocked sound autoplay → one tap turns it on (and remembers it) */}
        {autoPlayOnView && blocked && (
          <button
            type="button"
            data-sfx="none"
            onClick={toggleSound}
            className="absolute bottom-2 left-12 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-white text-[11px] font-bold anim-pop"
            style={{ background: BLUE }}
          >
            <FiVolume2 className="w-3.5 h-3.5" /> {t('tapForSound')}
          </button>
        )}

        {!inline && (
          <button
            type="button"
            data-sfx="pop"
            onClick={(e) => { e.stopPropagation(); setOpenRatio((v) => !v); }}
            className="absolute top-2 right-2 p-1.5 rounded-lg bg-black/45 text-white"
            aria-label="Resize"
          >
            <FiMaximize2 className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}
